<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { processVariable } from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: { type: Object, required: true },
  inboxId: { type: Number, required: true },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isSaving = ref(false);
const localMappings = ref({});

const SOURCES = [
  {
    value: 'manual',
    labelKey: 'WHATSAPP_TEMPLATES.VARIABLE_MAPPING.SOURCE_MANUAL',
  },
  {
    value: 'contact.name',
    labelKey: 'WHATSAPP_TEMPLATES.VARIABLE_MAPPING.SOURCE_CONTACT_NAME',
  },
  {
    value: 'contact.phone',
    labelKey: 'WHATSAPP_TEMPLATES.VARIABLE_MAPPING.SOURCE_CONTACT_PHONE',
  },
  {
    value: 'contact.cpf',
    labelKey: 'WHATSAPP_TEMPLATES.VARIABLE_MAPPING.SOURCE_CONTACT_CPF',
  },
];

const templateVariables = computed(() => {
  const body = props.template.components?.find(c => c.type === 'BODY');
  if (!body?.text) return [];
  const matches = body.text.match(/{{([^}]+)}}/g) || [];
  return [...new Set(matches.map(m => processVariable(m)))];
});

const initMappings = () => {
  const existing = props.template.variable_mappings || {};
  localMappings.value = templateVariables.value.reduce((acc, key) => {
    acc[key] = {
      label: existing[key]?.label || '',
      source: existing[key]?.source || 'manual',
    };
    return acc;
  }, {});
};

watch(() => props.template, initMappings, { immediate: true, deep: true });

const open = () => {
  initMappings();
  dialogRef.value?.open();
};

const handleSave = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('whatsappTemplates/update', {
      inboxId: props.inboxId,
      templateId: props.template.id,
      templateData: { variable_mappings: localMappings.value },
    });
    useAlert(t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.SAVE_SUCCESS'));
    emit('saved');
    dialogRef.value?.close();
  } catch (e) {
    useAlert(
      e.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const wrapVariable = key => `{{${key}}}`;

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.MODAL_TITLE')"
    :confirm-button-label="t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.SAVE_BUTTON')"
    :is-loading="isSaving"
    width="xl"
    overflow-y-auto
    @confirm="handleSave"
  >
    <div v-if="!templateVariables.length" class="py-4 text-sm text-n-slate-11">
      {{ t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.NO_VARIABLES') }}
    </div>
    <div v-else class="flex flex-col gap-3">
      <div
        class="grid grid-cols-3 gap-3 pb-2 text-xs font-medium text-n-slate-11 border-b border-n-weak"
      >
        <span>{{
          t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.VARIABLE_COLUMN')
        }}</span>
        <span>{{ t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.LABEL_COLUMN') }}</span>
        <span>{{
          t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.SOURCE_COLUMN')
        }}</span>
      </div>
      <div
        v-for="key in templateVariables"
        :key="key"
        class="grid grid-cols-3 gap-3 items-center"
      >
        <span class="font-mono text-sm text-n-slate-12">{{
          wrapVariable(key)
        }}</span>
        <Input
          v-model="localMappings[key].label"
          type="text"
          :placeholder="
            t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.LABEL_PLACEHOLDER')
          "
        />
        <select v-model="localMappings[key].source" class="mb-0">
          <option v-for="src in SOURCES" :key="src.value" :value="src.value">
            {{ t(src.labelKey) }}
          </option>
        </select>
      </div>
    </div>
  </Dialog>
</template>
